/**
 * A/B Test: Side-Cart Free Gift at $55 (manual "Add Free Gift")
 *
 * Cleanup tag: AB-SIDECART-FREEGIFT-2026-05
 * Spec: docs/superpowers/specs/2026-05-25-sidecart-free-gift-ab-test-design.md
 *
 * Control: current sidecart (free shipping at $45, no gift). No module runs.
 * Variant: this module, injected via Intelligems with a per-cell GIFT config.
 *   - Shows a "$55 Free Gift" milestone chip + dynamic banner.
 *   - At >= $55, shows a gift card with an "Add Free Gift" button.
 *   - Removes the gift if the cart subtotal drops back under $55 (the Shopify
 *     auto-discount stops applying below $55, so a lingering gift would be charged).
 *     Only the line this module added (tagged OWN_PROP) is ever removed. The same
 *     variant is also injected as a PDP subscription gift (gift_bundle
 *     brush-saver-dish) and sold on its own; those lines are not ours to touch.
 *
 * Gifts are real-priced products zeroed by a Shopify automatic discount
 * ("spend $55 -> product free"), configured per gift in admin (not in this code).
 *
 * Inject via Intelligems per test cell. No Liquid/CSS changes required.
 */
(function () {
  'use strict';

  // ─── Config ─────────────────────────────────────────────────────────────────
  // Evergreen theme config uses `window.__TEC_FREE_GIFT` and future Intelligems
  // threshold-test cells may layer `window.__TEC_FREE_GIFT_OVERRIDE` on top of it.
  // Existing paste files that set `window.__AB_FREE_GIFT` remain supported.
  var CFG = {};
  var THRESHOLD = 5500;              // gift unlock threshold (cents)
  var FREE_SHIP_THRESHOLD = 4500;    // theme-owned free shipping threshold (cents)
  var GIFT = {
    variantId: 0,                    // gift variant id to add to cart
    handle: '',                      // product handle for /products/{handle}.js
    title: '',                       // optional display-title override
    copy: ''                         // optional sub-copy line
  };
  var CELL = 'control';              // debug/analytics label
  // Line property marking the gift line this module added. Underscore prefix keeps
  // it hidden in the drawer and at checkout; no discount function reads it.
  var OWN_PROP = '_tec_gift';
  var OWN_VALUE = 'sidecart';

  // ─── Debug ───────────────────────────────────────────────────────────────────
  var DEBUG = false;                 // off unless config opts in
  var PREFIX = '[TEC-FreeGift:' + CELL + ']';

  function mergeObjects() {
    var out = {};
    for (var i = 0; i < arguments.length; i++) {
      var src = arguments[i] || {};
      for (var k in src) {
        if (!Object.prototype.hasOwnProperty.call(src, k)) continue;
        if (src[k] && typeof src[k] === 'object' && !Array.isArray(src[k])) {
          out[k] = mergeObjects(out[k], src[k]);
        } else {
          out[k] = src[k];
        }
      }
    }
    return out;
  }

  function normalizeConfig(raw) {
    raw = raw || {};
    var gift = raw.gift || {};
    var thresholds = raw.thresholds || {};
    return {
      mode: raw.mode || 'legacy',
      cell: raw.cell || 'control',
      debug: raw.debug === true,
      threshold: thresholds.gift || raw.threshold || 5500,
      freeShip: thresholds.freeShipping || raw.freeShip || 4500,
      gift: {
        variantId: gift.variantId || raw.variantId || 0,
        handle: gift.handle || raw.handle || '',
        title: gift.title || raw.title || '',
        copy: gift.copy || raw.copy || ''
      }
    };
  }

  function resolveConfig(extra) {
    var base = (typeof window !== 'undefined' && window.__TEC_FREE_GIFT) || {};
    var override = (typeof window !== 'undefined' && window.__TEC_FREE_GIFT_OVERRIDE) || {};
    var legacy = (typeof window !== 'undefined' && window.__AB_FREE_GIFT) || {};
    // Legacy config is treated as a full config when no evergreen config exists.
    // Otherwise it behaves like an override, preserving current Intelligems paste files.
    if (!base || !base.gift) base = legacy;
    return normalizeConfig(mergeObjects(base, override, extra || {}));
  }

  function applyConfig(extra) {
    CFG = resolveConfig(extra);
    THRESHOLD = CFG.threshold;
    FREE_SHIP_THRESHOLD = CFG.freeShip;
    GIFT = CFG.gift;
    CELL = CFG.cell;
    DEBUG = CFG.debug;
    PREFIX = '[TEC-FreeGift:' + CELL + ']';
    productCache = new Map();
    lastCart = null;
    lastState = null;
    try {
      appliedOverrideSig = JSON.stringify((typeof window !== 'undefined' && window.__TEC_FREE_GIFT_OVERRIDE) || {});
    } catch (e) { appliedOverrideSig = null; }
    log('config applied', CFG);
  }
  function log() {
    if (!DEBUG) return;
    console.log.apply(console, [PREFIX].concat([].slice.call(arguments)));
  }

  // ─── Caches & state ────────────────────────────────────────────────────────
  var productCache = new Map(); // handle → product json
  var lastState = null;         // last computed state — used to re-apply our DOM synchronously after a theme rebuild (anti-flicker)
  var appliedOverrideSig = null; // signature of the override last folded into CFG — detects late IG injections
  var lastCart = null;
  var lastCartFetch = 0;
  var sweepInFlight = false;
  var mutating = false;         // true while we add/remove the gift ourselves

  // ─── Money formatter ───────────────────────────────────────────────────────
  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      try { return Shopify.formatMoney(cents); } catch (e) {}
    }
    return '$' + (Math.round(cents) / 100).toFixed(2);
  }

  // ─── DOM helper ──────────────────────────────────────────────────────────────
  function el(tag, styles, attrs) {
    var node = document.createElement(tag);
    if (styles) node.style.cssText = styles;
    if (attrs) for (var k in attrs) node.setAttribute(k, attrs[k]);
    return node;
  }

  // ─── HTML escaping ───────────────────────────────────────────────────────────
  // title/copy can be supplied by an Intelligems override cell, so never trust them
  // raw in innerHTML.
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // ─── Manual-dismissal memory ─────────────────────────────────────────────────
  // If the shopper deletes the gift line while still above threshold, respect that:
  // stop re-offering the card until they drop below threshold and cross it again.
  // Stored per variant so switching the configured gift re-arms the offer.
  var DISMISS_KEY = 'tecFreeGiftDismissed';
  function isDismissed() {
    try { return sessionStorage.getItem(DISMISS_KEY) === String(GIFT.variantId); } catch (e) { return false; }
  }
  function setDismissed(on) {
    try {
      if (on) sessionStorage.setItem(DISMISS_KEY, String(GIFT.variantId));
      else sessionStorage.removeItem(DISMISS_KEY);
    } catch (e) {}
  }

  // ─── Fetch helpers ───────────────────────────────────────────────────────────
  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache.has(handle)) return Promise.resolve(productCache.get(handle));
    return fetch('/products/' + handle + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { productCache.set(handle, p); return p; })
      .catch(function () { productCache.set(handle, null); return null; });
  }

  function fetchCart() {
    var now = Date.now();
    if (lastCart && now - lastCartFetch < 250) return Promise.resolve(lastCart);
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { lastCart = cart; lastCartFetch = Date.now(); return cart; });
  }

  // ─── Cart math (pure) ─────────────────────────────────────────────────────
  // Subtotal of everything EXCEPT the gift line, in cents. Uses final_line_price
  // so existing discounts are respected. Excluding the gift prevents the gift
  // from keeping itself "unlocked."
  function subtotalExcludingGift(cart) {
    var sum = 0;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].variant_id === GIFT.variantId) continue;
      sum += items[i].final_line_price;
    }
    return sum;
  }

  // Returns { unlocked, remainingCents, giftInCart, giftKey }.
  // giftInCart: ANY line of the gift variant (ours, a PDP gift, or a paid one), so
  //   we never offer a second one on top.
  // giftKey: only the line WE added (OWN_PROP), the only one the guard may remove.
  function computeState(cart) {
    var subtotal = subtotalExcludingGift(cart);
    var giftInCart = false;
    var giftKey = null;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].variant_id !== GIFT.variantId) continue;
      giftInCart = true;
      if (items[i].properties && items[i].properties[OWN_PROP] === OWN_VALUE) { giftKey = items[i].key; break; }
    }
    return {
      subtotal: subtotal,
      unlocked: subtotal >= THRESHOLD,
      remainingCents: Math.max(0, THRESHOLD - subtotal),
      giftInCart: giftInCart,
      giftKey: giftKey
    };
  }

  // ─── Renderers ─────────────────────────────────────────────────────────────
  // Theme icons (verbatim from assets/icon-lock.svg + icon-green-checkmark.svg) so
  // the Free Gift chip matches the Free Shipping chip exactly. CSS sizes them to 17x19.
  var LOCK  = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96.108 122.88" width="17" height="19" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M2.892,56.036h8.959v-1.075V37.117c0-10.205,4.177-19.484,10.898-26.207v-0.009 C29.473,4.177,38.754,0,48.966,0C59.17,0,68.449,4.177,75.173,10.901l0.01,0.009c6.721,6.723,10.898,16.002,10.898,26.207v17.844 v1.075h7.136c1.59,0,2.892,1.302,2.892,2.891v61.062c0,1.589-1.302,2.891-2.892,2.891H2.892c-1.59,0-2.892-1.302-2.892-2.891 V58.927C0,57.338,1.302,56.036,2.892,56.036L2.892,56.036z M26.271,56.036h45.387v-1.075V36.911c0-6.24-2.554-11.917-6.662-16.03 l-0.005,0.004c-4.111-4.114-9.787-6.669-16.025-6.669c-6.241,0-11.917,2.554-16.033,6.665c-4.109,4.113-6.662,9.79-6.662,16.03 v18.051V56.036L26.271,56.036z M49.149,89.448l4.581,21.139l-12.557,0.053l3.685-21.423c-3.431-1.1-5.918-4.315-5.918-8.111 c0-4.701,3.81-8.511,8.513-8.511c4.698,0,8.511,3.81,8.511,8.511C55.964,85.226,53.036,88.663,49.149,89.448L49.149,89.448z"/></svg>';
  var CHECK = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" width="17" height="19" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" fill="#6BBE66" d="M48,0c26.51,0,48,21.49,48,48S74.51,96,48,96S0,74.51,0,48 S21.49,0,48,0L48,0z M26.764,49.277c0.644-3.734,4.906-5.813,8.269-3.79c0.305,0.182,0.596,0.398,0.867,0.646l0.026,0.025 c1.509,1.446,3.2,2.951,4.876,4.443l1.438,1.291l17.063-17.898c1.019-1.067,1.764-1.757,3.293-2.101 c5.235-1.155,8.916,5.244,5.206,9.155L46.536,63.366c-2.003,2.137-5.583,2.332-7.736,0.291c-1.234-1.146-2.576-2.312-3.933-3.489 c-2.35-2.042-4.747-4.125-6.701-6.187C26.993,52.809,26.487,50.89,26.764,49.277L26.764,49.277z"/></svg>';

  function renderChip(state) {
    var header = document.querySelector('#CartDrawer .drawer__header');
    if (!header) return;
    var chip = header.querySelector('.ab-free-gift-chip');
    if (!chip) {
      // Same classes as the theme's Free Shipping chip → identical font (Nitti) + casing.
      chip = el('div', null, { 'class': 'drawer__header__item font-nitti uppercase ab-free-gift-chip' });
      header.appendChild(chip);
    }
    // Positioning: when the gift unlocks BEFORE free shipping (gift threshold below
    // the shipping threshold, e.g. the $35/$40 cells) the gift is the nearer reward,
    // so it should lead — placed before the native Free Shipping chip. Otherwise it
    // trails shipping, matching the evergreen $55 behavior.
    var shippingChip = header.querySelector('.drawer__header__item--free-shipping');
    if (shippingChip && shippingChip.parentNode) {
      var giftFirst = THRESHOLD < FREE_SHIP_THRESHOLD;
      if (giftFirst && chip.nextSibling !== shippingChip) {
        shippingChip.parentNode.insertBefore(chip, shippingChip);
      } else if (!giftFirst && shippingChip.nextSibling !== chip) {
        shippingChip.parentNode.insertBefore(chip, shippingChip.nextSibling);
      }
    }
    chip.innerHTML = (state.unlocked ? CHECK : LOCK) + '<span>Free <br>Gift</span>';
  }

  function renderBanner(state) {
    var banner = document.querySelector('#CartDrawer .drawer__message');
    if (!banner) return;
    var subtotal = state.subtotal;
    var msg;

    if (THRESHOLD === FREE_SHIP_THRESHOLD) {
      // Gift unlocks exactly at the free-shipping milestone (e.g. the $45 cell).
      msg = state.unlocked
        ? 'Free shipping + free gift unlocked 🎉'
        : 'Add ' + formatMoney(THRESHOLD - subtotal) + ' for free shipping + a free gift';
    } else if (THRESHOLD < FREE_SHIP_THRESHOLD) {
      // Gift is the NEARER reward (e.g. $35/$40 cells) → lead with the gift.
      if (!state.unlocked) {
        msg = 'Add ' + formatMoney(state.remainingCents) + ' for your free gift';
      } else if (subtotal < FREE_SHIP_THRESHOLD) {
        // Gift already unlocked (shown by the checkmark chip) → only show the next step.
        msg = 'Add ' + formatMoney(FREE_SHIP_THRESHOLD - subtotal) + ' for free shipping';
      } else {
        msg = 'Free gift + free shipping unlocked 🎉';
      }
    } else {
      // Free shipping is the nearer reward (evergreen $55, $60 cells) → lead with shipping.
      if (subtotal < FREE_SHIP_THRESHOLD) {
        msg = 'Add ' + formatMoney(FREE_SHIP_THRESHOLD - subtotal) + ' for Free Shipping!';
      } else if (!state.unlocked) {
        // Free shipping already unlocked (shown by the checkmark chip) → only show the next step.
        msg = 'Add ' + formatMoney(state.remainingCents) + ' for your free gift';
      } else {
        msg = 'Free gift unlocked 🎉';
      }
    }

    // Multi-line messages use '\n' → render as <br> (escaped; these are internal strings).
    if (msg.indexOf('\n') !== -1) {
      banner.innerHTML = msg.split('\n').map(escapeHtml).join('<br>');
    } else {
      banner.textContent = msg;
    }
    banner.setAttribute('data-ab-free-gift', '1');
  }

  function findInsertionTarget() {
    var drawerContent = document.querySelector('#CartDrawer .drawer__content');
    if (!drawerContent) return null;
    // Place the gift card at the TOP of the cart, before the line items, so it's visible.
    var items = drawerContent.querySelector('cart-drawer-items');
    return { parent: drawerContent, before: items || drawerContent.firstChild };
  }

  function removeGiftCard() {
    var existing = document.querySelector('.ab-free-gift-card');
    if (existing) existing.parentNode.removeChild(existing);
  }

  // product: result of /products/{handle}.js (for image + availability).
  function renderGiftCard(product) {
    var target = findInsertionTarget();
    if (!target) return;
    if (!product || product.available === false) { removeGiftCard(); return; }

    var img = (product.featured_image) ? (product.featured_image + '&width=160') : '';
    var title = GIFT.title || product.title || 'Free gift';

    var card = el('cart-free-gift', null, { 'class': 'cart__free-gift ab-free-gift-card', 'data-variant-id': GIFT.variantId });
    card.innerHTML =
      '<div class="cart__free-gift__image">' +
        (img ? '<img class="cart__free-gift__image-element" src="' + img + '" alt="" loading="lazy" width="80">' : '') +
      '</div>' +
      '<div class="cart__free-gift__title">' + escapeHtml(title) + (GIFT.copy ? '<br><small>' + escapeHtml(GIFT.copy) + '</small>' : '') + '</div>' +
      '<button type="button" class="cart__free-gift__submit font-nitti uppercase"><span>Add Free Gift</span></button>';

    var submit = card.querySelector('.cart__free-gift__submit');
    if (submit) submit.addEventListener('click', function (e) { e.preventDefault(); addGift(submit); });

    var existing = document.querySelector('.ab-free-gift-card');
    if (existing) {
      existing.replaceWith(card);
    } else if (target.before) {
      target.parent.insertBefore(card, target.before);
    } else {
      target.parent.appendChild(card);
    }
    return card;
  }

  function addGift(button) {
    if (mutating) return;
    mutating = true;
    if (button) button.classList.add('loading');
    function done() { mutating = false; if (button) button.classList.remove('loading'); }
    // Re-check the cart first: never POST a second gift line if one is already
    // present (double-click, or a stale button left over from a drawer re-render).
    fetchCart()
      .then(function (cart) {
        if (computeState(cart).giftInCart) { log('gift already in cart — skip add'); setTimeout(sweep, 0); return null; }
        var props = {};
        props[OWN_PROP] = OWN_VALUE;
        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          // No selling_plan key → gift is always added as a one-time line, never a subscription.
          body: JSON.stringify({ items: [ { id: GIFT.variantId, quantity: 1, properties: props } ] })
        })
          .then(function (r) {
            if (!r.ok) throw new Error('cart/add ' + r.status);
            return r.json();
          })
          .then(function () {
            lastCart = null;
            try {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ab-free-gift' });
              }
            } catch (e) {}
            setTimeout(sweep, 200);
          });
      })
      .catch(function (e) { console.error(PREFIX, 'addGift error', e); })
      .then(done);
  }

  function removeGift(giftKey) {
    if (mutating || !giftKey) return Promise.resolve();
    mutating = true;
    log('guard: removing gift line', giftKey, '(subtotal under threshold)');
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: giftKey, quantity: 0 })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart/change ' + r.status);
        return r.json();
      })
      .then(function () {
        lastCart = null;
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ab-free-gift' });
          }
        } catch (e) {}
      })
      .catch(function (e) { console.error(PREFIX, 'removeGift error', e); })
      .then(function () { mutating = false; });
  }

  // ─── Orchestrator ────────────────────────────────────────────────────────────
  function sweep() {
    if (sweepInFlight) return;
    // Late-injection guard: if Intelligems set/changed window.__TEC_FREE_GIFT_OVERRIDE
    // after init() ran, fold it in now so the cell's threshold/messaging take effect
    // even though the snippet executed before this module finished loading.
    try {
      var liveSig = JSON.stringify(window.__TEC_FREE_GIFT_OVERRIDE || {});
      if (liveSig !== appliedOverrideSig) { applyConfig(); }
    } catch (e) {}
    if (!GIFT.variantId || !GIFT.handle) { log('no gift configured — control behavior'); return; }
    sweepInFlight = true;
    fetchCart()
      .then(function (cart) {
        var state = computeState(cart);
        var prev = lastState;
        lastState = state;
        log('state', state);

        // Shopper deleted the gift line while still above threshold → remember it
        // and stop re-offering the card (re-armed when they drop below threshold).
        // Our own below-threshold removal is excluded by the state.unlocked check.
        if (prev && prev.giftInCart && !state.giftInCart && state.unlocked && !mutating) {
          setDismissed(true);
        }
        if (!state.unlocked) setDismissed(false);

        // Guard: OUR gift line is in cart but subtotal fell under $55 → remove it so
        // the auto-discount lapse never charges the customer. Re-sweep after removal.
        // A PDP subscription gift of the same variant has no OWN_PROP (giftKey null)
        // and is left alone: removing it dropped the free bag from sub first orders.
        if (state.giftKey && !state.unlocked) {
          return removeGift(state.giftKey).then(function () { setTimeout(sweep, 200); });
        }

        renderChip(state);
        renderBanner(state);
        if (state.unlocked && !state.giftInCart && !isDismissed()) {
          return fetchProduct(GIFT.handle).then(function (product) {
            renderGiftCard(product);
          });
        } else {
          removeGiftCard();
        }
      })
      .catch(function (err) { console.error(PREFIX, 'sweep error', err); })
      .then(function () { sweepInFlight = false; watchDrawer(); });
  }

  // ─── AJAX patch — re-sweep after any /cart WRITE ───────────────────────────
  var CART_WRITE_RE = /\/cart\/(change|add|update|clear)(\.js)?(\?|$)/;
  function patchAjax() {
    var origFetch = window.fetch;
    if (origFetch && !origFetch.__abFreeGiftPatched) {
      window.fetch = function () {
        var args = arguments;
        var url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
        if (!CART_WRITE_RE.test(url)) return origFetch.apply(this, args);
        return origFetch.apply(this, args).then(function (res) {
          lastCart = null;
          setTimeout(sweep, 60);
          setTimeout(sweep, 350);
          return res;
        });
      };
      window.fetch.__abFreeGiftPatched = true;
    }
  }

  // ─── Anti-flicker: re-apply our elements synchronously after a theme rebuild ──
  // The theme replaces #CartDrawer's entire innerHTML on cart changes, wiping our
  // chip/banner/card. The MutationObserver fires as a microtask BEFORE the browser
  // paints, so re-injecting here (from the last known state) means nothing visibly
  // disappears. sweep() then corrects the numbers a beat later. No network here —
  // sweeps are driven by the cartUpdate subscription + the /cart fetch hook, so the
  // once-a-second urgency timer can't trigger a fetch storm.
  function reapplyFromCache() {
    if (!lastState) return;
    var header = document.querySelector('#CartDrawer .drawer__header');
    if (header && !header.querySelector('.ab-free-gift-chip')) renderChip(lastState);
    var banner = document.querySelector('#CartDrawer .drawer__message');
    if (banner && banner.getAttribute('data-ab-free-gift') !== '1') renderBanner(lastState);
    if (lastState.unlocked && !lastState.giftInCart && !isDismissed() && !document.querySelector('.ab-free-gift-card')) {
      var product = productCache.get(GIFT.handle);
      if (product) renderGiftCard(product);
    }
  }

  // ─── MutationObserver — keep our elements present across drawer re-renders ─────
  function watchDrawer() {
    var target = document.getElementById('CartDrawer');
    if (!target || target.__abFreeGiftWatched) return;
    target.__abFreeGiftWatched = true;
    new MutationObserver(reapplyFromCache).observe(target, { childList: true, subtree: true });
  }

  // ─── Injected styles — widen the button so "ADD FREE GIFT" fits on one line ───
  function injectStyles() {
    if (document.getElementById('ab-free-gift-styles')) return;
    var s = document.createElement('style');
    s.id = 'ab-free-gift-styles';
    s.textContent =
      '.ab-free-gift-card{grid-template-columns:90px 1fr minmax(150px,40%) !important;padding-left:14px !important;padding-right:14px !important;}' +
      '.ab-free-gift-card .cart__free-gift__submit{padding-inline:8px !important;white-space:nowrap !important;}' +
      // Match the 20px side padding of the cart content below; allow 2 lines without clipping.
      '.drawer__message[data-ab-free-gift]{padding:8px 20px !important;height:auto !important;min-height:55px;box-sizing:border-box;text-align:center;line-height:1.25;}' +
      '@media (width < 400px){.drawer__message[data-ab-free-gift]{min-height:44px;}}';
    document.head.appendChild(s);
  }

  // ─── Public API for Intelligems threshold cells ─────────────────────────────
  function exposeApi() {
    window.TECFreeGift = window.TECFreeGift || {};
    window.TECFreeGift.configure = function (config) {
      window.__TEC_FREE_GIFT_OVERRIDE = mergeObjects(window.__TEC_FREE_GIFT_OVERRIDE || {}, config || {});
      applyConfig();
      sweep();
    };
    window.TECFreeGift.sweep = sweep;
  }

  // ─── Init ──────────────────────────────────────────────────────────────────
  function init() {
    applyConfig();
    exposeApi();
    injectStyles();
    sweep();
    patchAjax();
    watchDrawer();
    // Re-render the instant the theme finishes a cart update (no waiting on timers).
    // The theme publishes cartUpdate AFTER it rebuilds the drawer, which wipes our
    // injected chip/card — this re-adds them immediately. Bare globals: PUB_SUB_EVENTS
    // is a classic-script global, reachable lexically (not on window).
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () { lastCart = null; watchDrawer(); sweep(); });
      }
    } catch (e) {}
    log('init complete');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('load', function () {
    setTimeout(function () { sweep(); watchDrawer(); }, 300);
    setTimeout(function () { sweep(); watchDrawer(); }, 1500);
  });

  // Re-sweep a few times after late asset loads (IG injection can land early).
  [600, 1200, 2500].forEach(function (ms) {
    setTimeout(function () { sweep(); watchDrawer(); }, ms);
  });
})();
